// ── Design decisions ──
// Parser determinista de listas pegadas de WhatsApp: cero red, cero estado,
// cero costo. Produce EXACTAMENTE la misma forma que la ruta de foto
// (extractLista ya normalizado): { extracted: { paquetes, extraction_notes,
// requires_review }, requires_review, cached, content_hash, … } con ítems
// { description_raw, qty, unit_type, unit_price, is_new, notes,
// requires_review } — mapearResultado (ExtractionDrawer) los consume por sus
// cadenas bilingües sin saber de qué ruta vinieron.
//
// · Cero descartes silenciosos: cada línea del pegado termina clasificada
//   (encabezado / bulto / dueño / artículo / vacía) o adjunta como artículo
//   con requires_review — el parser nunca inventa y nunca tira.
// · La ambigüedad se marca, no se adivina: un token suelto sin dígito inicial
//   (CINCHO) jamás abre paquete; entra como artículo flagueado del paquete
//   actual. Un dueño inmediatamente después de otro dueño también queda
//   flagueado (requires_review en el paquete + nota), porque en las listas
//   reales eso suele ser un artículo sin cantidad o un nombre repetido.
// · mapearResultado solo conserva owner_name_raw + items de cada paquete: el
//   tag de referencia (36H, #116) y la maleta/bulto de contexto NO
//   sobrevivirían como campos del paquete, así que además de ir en el objeto
//   del parser (para tests y futuros consumidores) se agregan a
//   extraction_notes, que el drawer sí muestra. El tag NUNCA toca
//   owner_name_raw: contarTokensNombre cuenta dígitos como tokens y un "36h"
//   dentro del nombre rompería la auto-asignación exacta.
// · unit_price siempre null: la gramática de WhatsApp no trae precios y la
//   doctrina es no inventarlos — mapearResultado convierte ese null en FOB 0 +
//   requires_review ("Sin precio en la lista"), igual que la ruta de foto.
// · Los sustantivos de calzado se excluyen del detector de nombre-suelto
//   ("TENIS NIKE" no es una persona): mejor un artículo flagueado que un
//   paquete fantasma.
// · Sin dependencias: regex + tokenizador a mano, insensible a acentos y
//   mayúsculas (se normaliza para CLASIFICAR, pero descripción y nombre salen
//   con el texto original intacto — los marcadores M/P, O/M, M/L viajan en la
//   descripción porque el matcher de catálogo los usa).

// Sin acentos, minúsculas, trim — solo para clasificar; nunca para mostrar.
const normalizar = (s) =>
  String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();

const UNIDAD_MAP = [
  [/^kgs?$|^kilos?$/, 'KG'],
  [/^lbs?$|^libras?$/, 'LB'],
  [/^par$|^pares$/, 'PAR'],
  [/^docenas?$/, 'DOCENA'],
  [/^juegos?$/, 'JUEGO'],
  [/^unidad$|^unidades$/, 'UNIDAD'],
];

const esUnidad = (tokenNorm) => {
  const limpio = tokenNorm.replace(/\.+$/, '');
  const hit = UNIDAD_MAP.find(([re]) => re.test(limpio));
  return hit ? hit[1] : null;
};

// Cabezas de calzado: infieren PAR sin unidad explícita y descalifican el
// detector de nombre-suelto.
const CALZADO_RE = /^(tenis|zapatos?|botas?|sandalias?|chanclas?)$/;

// Conectores de nombre que pueden ir en minúscula ("Ana Gutierrez de Ramos").
const CONECTORES = new Set(['de', 'del', 'la', 'las', 'los', 'san', 'santa', 'y']);

// Tag de referencia de las listas reales: "36H." / "AC12" / "#116". Debe
// mezclar dígitos con # o letras — un número pelado al inicio es una cantidad.
const TAG_RE = /^(#\d+[a-z]?|\d+[a-z]+|[a-z]{1,4}\d+[a-z]?)[.:\-)]?$/i;

// ¿El resto de la línea parece un nombre de persona? Solo letras (con
// acentos), 1–5 tokens, sin dígitos ni palabras de unidad.
const pareceNombre = (texto) => {
  const toks = normalizar(texto).split(/\s+/).filter(Boolean);
  if (toks.length === 0 || toks.length > 5) return false;
  return toks.every(
    (t) => /^[a-zñ]+\.?$/.test(t) && !esUnidad(t) && !CALZADO_RE.test(t.replace(/\.$/, ''))
  );
};

// Nombre "suelto" (sin tag): 2–3 tokens capitalizados sin dígitos ni unidades;
// los conectores pueden ir en minúscula y no cuentan para el límite.
const esNombreSuelto = (original) => {
  const toks = original.trim().split(/\s+/).filter(Boolean);
  if (toks.length < 2) return false;
  const principales = toks.filter((t) => !CONECTORES.has(normalizar(t)));
  if (principales.length < 2 || principales.length > 3) return false;
  return (
    toks.every((t) => {
      const norm = normalizar(t);
      if (/\d/.test(t)) return false;
      // "ROPA USADA" / "CAMISA NUEVA" no son personas: los adjetivos de
      // condición descalifican el nombre suelto (quedan como línea ambigua).
      if (/^usad[oa]s?$|^nuev[oa]s?$/.test(norm)) return false;
      if (esUnidad(norm) || CALZADO_RE.test(norm)) return false;
      if (CONECTORES.has(norm)) return true;
      return /^[A-ZÁÉÍÓÚÑÜ]/.test(t.trim()) && /^[a-zñ.]+$/.test(norm.replace(/\.$/, ''));
    })
  );
};

// ── Clasificador de línea (exportado para tests) ─────────────────────────────
// Devuelve { tipo, ...datos }; tipos: 'vacia' | 'encabezado' | 'bulto' |
// 'dueno' | 'item' | 'ambigua'. Puro: no conoce el contexto de la lista.
export const clasificarLinea = (linea) => {
  const original = String(linea ?? '').trim();
  if (!original) return { tipo: 'vacia' };
  const norm = normalizar(original);

  // Encabezado de declaración: nota del viaje, nunca un paquete.
  if (/^declaracion\b/.test(norm)) {
    const m = norm.match(/\bde\s+([a-zñ\s]+)$/);
    return { tipo: 'encabezado', texto: original, nombre: m ? m[1].trim() : null };
  }

  // Bulto / maleta / caja numerada: abre agrupación de contexto.
  if (/^(maleta|bulto|caja)\s*\d+$/.test(norm)) {
    return { tipo: 'bulto', etiqueta: original };
  }

  const toks = original.split(/\s+/).filter(Boolean);

  // Dueño con tag ("36H. LILIAN GUERRA"). Corre ANTES que el artículo: "36H"
  // no es una cantidad. Un número pelado ("116 Cesar…") NO es tag: cantidad.
  if (toks.length >= 2 && TAG_RE.test(toks[0]) && !/^\d+[.:\-)]?$/.test(toks[0])) {
    const resto = toks.slice(1).join(' ');
    if (pareceNombre(resto)) {
      return {
        tipo: 'dueno',
        nombre: resto,
        tag: toks[0].replace(/[.:\-)]$/, ''),
      };
    }
  }

  // Artículo: cantidad al frente ("4 KGS ROPA USADA", "1 par de tenis m/p").
  const mItem = original.match(/^(\d+(?:[.,]\d+)?)[.:\-)]?\s+(.+)$/);
  if (mItem && /^\d+(?:[.,]\d+)?[.:\-)]?$/.test(toks[0])) {
    const qty = Number(mItem[1].replace(',', '.')) || 1;
    let restoToks = mItem[2].trim().split(/\s+/).filter(Boolean);

    // Unidad explícita al frente (+ "de" opcional: "par de tenis").
    let unidad = null;
    if (restoToks.length > 0) {
      const u = esUnidad(normalizar(restoToks[0]));
      if (u) {
        unidad = u;
        restoToks = restoToks.slice(1);
        if (restoToks.length > 0 && normalizar(restoToks[0]) === 'de') restoToks = restoToks.slice(1);
      }
    }

    let descripcion = restoToks.join(' ');

    // Notas entre paréntesis: salen de la descripción y flaguean revisión.
    const notas = [];
    descripcion = descripcion
      .replace(/\(([^)]*)\)/g, (_, nota) => {
        const limpia = nota.trim();
        if (limpia) notas.push(limpia);
        return ' ';
      })
      .replace(/\s+/g, ' ')
      .trim();

    // Sin unidad explícita: cabeza de calzado ⇒ PAR.
    if (!unidad) {
      const cabeza = normalizar(descripcion).split(/\s+/)[0] || '';
      unidad = CALZADO_RE.test(cabeza) ? 'PAR' : 'UNIDAD';
    }

    // USADO/USADA apaga is_new; el token queda en la descripción (el matcher
    // de catálogo lo usa). NUEVO es el default y también se conserva.
    const descToks = normalizar(descripcion).split(/\s+/);
    const is_new = !descToks.some((t) => /^usad[oa]s?$/.test(t));

    return {
      tipo: 'item',
      qty,
      unit_type: unidad,
      descripcion,
      is_new,
      notes: notas.length > 0 ? notas.join(' · ') : null,
      // Paréntesis ⇒ el operador decide; descripción vacía ⇒ también.
      requires_review: notas.length > 0 || descripcion === '',
    };
  }

  // Nombre suelto capitalizado (2–3 tokens): dueño.
  if (esNombreSuelto(original)) {
    return { tipo: 'dueno', nombre: original, tag: null };
  }

  // Todo lo demás — token suelto (CINCHO), emojis, URLs, garabatos — es
  // ambiguo: artículo flagueado, nunca un dueño adivinado, nunca un descarte.
  return { tipo: 'ambigua', texto: original };
};

// ── Parser de lista completa ─────────────────────────────────────────────────
export const parseListaTexto = (texto) => {
  const lineas = String(texto ?? '').split(/\r\n|\r|\n/);

  const paquetes = [];
  const notas = [];
  const refs = [];
  const bultoOwners = new Map(); // etiqueta de bulto → [nombres]
  let bultoActual = null;
  let paqueteActual = null;
  let prevFueDueno = false;

  const stats = { lineas: lineas.length, vacias: 0, encabezados: 0, bultos: 0, duenos: 0, items: 0, ambiguas: 0, descartadas: 0 };

  // Paquete de resguardo para artículos que llegan antes del primer nombre:
  // owner_name_raw vacío (el drawer lo muestra como "Sin nombre" y su falta de
  // cliente bloquea Confirmar sola).
  const asegurarPaquete = () => {
    if (paqueteActual) return paqueteActual;
    paqueteActual = { owner_name_raw: '', reference_tag: null, bulto: bultoActual, requires_review: true, items: [] };
    paquetes.push(paqueteActual);
    notas.push('Hay artículos antes del primer nombre: quedaron en un paquete sin nombre');
    return paqueteActual;
  };

  lineas.forEach((linea) => {
    const c = clasificarLinea(linea);
    switch (c.tipo) {
      case 'vacia':
        stats.vacias += 1;
        break;
      case 'encabezado':
        stats.encabezados += 1;
        notas.push(`Encabezado: ${c.texto}`);
        break;
      case 'bulto':
        stats.bultos += 1;
        bultoActual = c.etiqueta;
        if (!bultoOwners.has(c.etiqueta)) bultoOwners.set(c.etiqueta, []);
        prevFueDueno = false;
        break;
      case 'dueno': {
        stats.duenos += 1;
        const consecutivo = prevFueDueno;
        paqueteActual = {
          owner_name_raw: c.nombre,
          reference_tag: c.tag,
          bulto: bultoActual,
          requires_review: consecutivo,
          items: [],
        };
        paquetes.push(paqueteActual);
        if (bultoActual) bultoOwners.get(bultoActual)?.push(c.nombre);
        if (c.tag) refs.push(`${c.tag} → ${c.nombre}`);
        if (consecutivo) {
          notas.push(`Revisar: "${c.nombre}" viene justo después de otro nombre (¿artículo o persona?)`);
        }
        prevFueDueno = true;
        return; // no tocar prevFueDueno abajo
      }
      case 'item': {
        stats.items += 1;
        asegurarPaquete().items.push({
          description_raw: c.descripcion,
          qty: c.qty,
          unit_type: c.unit_type,
          unit_price: null,
          is_new: c.is_new,
          notes: c.notes,
          requires_review: c.requires_review,
        });
        break;
      }
      case 'ambigua': {
        stats.ambiguas += 1;
        asegurarPaquete().items.push({
          description_raw: c.texto,
          qty: 1,
          unit_type: 'UNIDAD',
          unit_price: null,
          is_new: true,
          notes: 'Línea ambigua: revisá si es artículo, nombre u otra cosa',
          requires_review: true,
        });
        break;
      }
      default:
        break;
    }
    prevFueDueno = false;
  });

  // Notas del viaje: encabezado + mapa bulto→dueños + tags de referencia.
  bultoOwners.forEach((owners, etiqueta) => {
    notas.push(owners.length > 0 ? `${etiqueta}: ${owners.join(', ')}` : etiqueta);
  });
  if (refs.length > 0) notas.push(`Refs: ${refs.join(' · ')}`);

  return {
    // Misma forma normalizada que devuelve extractLista (ruta de foto):
    // extraction_notes vive DENTRO de extracted — es lo que lee el drawer.
    extracted: {
      paquetes,
      extraction_notes: notas.join(' · '),
      requires_review: true,
    },
    requires_review: true,
    usage: null,
    model: null,
    cached: false,
    content_hash: null,
    source: 'texto',
    stats,
  };
};

export default parseListaTexto;
